import { useMemo, useState } from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router';

import { Box, CircularProgress } from '@mui/material';
import { CareType, MatchingProvidersType } from '@uplift/api';
import { ROUTING_CONFIG, BETTERHELP_ONBOARDING_ROUTING_CONFIG } from '@uplift/constants';
import { useCurrentuserRefetch } from '@uplift/contexts';
import { useStore } from '@uplift/hooks';
import { ProviderMedicalRole } from '@uplift/types';

import { Matches } from '../../components';
import { YourProviderBanner } from '../components';
import { OnboardingLayout } from '../components/layout';
import { ProgressBar } from '../components/progress-bar';
import {
  Age, ConfirmBooking, Match, NotAvailable, PaymentInfo, ProviderExperienceFilter, ProviderFilters,
  ProviderIdentityFilter, Reason
} from '../default/steps';
import { SELF_PAY_ID } from '../utils';
import { useBetterhelpOnboardingMachine } from './machine/use-machine';
import { getEligibility } from './machine/utils';
import {
  AboutYou, BookingInterstitialPage, BookingSuccess,
  CreateAccount, Insurance, InvalidLink, MatchesLoading, TooYoung, VerifyPhone,
  VerifyPhoneCode, Welcome, Where
} from './steps';
import { InsuranceInfo } from '../default/steps/insurance-info';

const fullScreenRoutes: string[] = [
  BETTERHELP_ONBOARDING_ROUTING_CONFIG.welcome,
  BETTERHELP_ONBOARDING_ROUTING_CONFIG.matches,
  BETTERHELP_ONBOARDING_ROUTING_CONFIG.match,
];
const hideProgressBarRoutes: string[] = [
  BETTERHELP_ONBOARDING_ROUTING_CONFIG.welcome,
  BETTERHELP_ONBOARDING_ROUTING_CONFIG.matches,
  BETTERHELP_ONBOARDING_ROUTING_CONFIG.match,
  BETTERHELP_ONBOARDING_ROUTING_CONFIG.matchesLoading,
];

export const BetterhelpOnboardingPage: React.FC = () => {
  const history = useHistory();
  const { authStore } = useStore();
  const [ isExiting, setIsExiting ] = useState(false);
  const path = useLocation().pathname;
  const { state, send } = useBetterhelpOnboardingMachine({ isExiting });
  const { refetchUser } = useCurrentuserRefetch();
  const isOnboardingCompleted = state.context?.termsAccepted;
  const eligibility = getEligibility(state.context);

  const onExit = async () => {
    setIsExiting(true);
    localStorage.removeItem('betterhelp-matching');

    if (isOnboardingCompleted) {
      await refetchUser();
      await authStore.fetchAccessTokenByRefreshToken();
      history.push(ROUTING_CONFIG.main);
      return;
    }

    authStore.logout('https://joinuplift.co/');
  };

  const onBack = () => send({ type: 'Back' });

  const fullScreen = fullScreenRoutes.includes(path);
  const showProgressBar = !hideProgressBarRoutes.includes(path);

  const banner = useMemo(() => {
    const providerId = state.context.selectedProviderId;
    const appointment = state.context.appointment;

    return (
      <YourProviderBanner
        providerId={providerId}
        appointment={appointment}
      />
    );
  }, [ state.context.appointment, state.context.selectedProviderId ]);

  if (state.matches('Initial') || state.matches('Idle') || isExiting) {
    return (
      <OnboardingLayout
        fullScreen={fullScreen}
        onBack={() => {
          return;
        }}
        onExit={onExit}
        hasBackButton={false}
        isOnboardingCompleted={isOnboardingCompleted}
      >
        <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
          <CircularProgress color="secondary" />
        </Box>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      hasBackButton={state.can({ type: 'Back' })}
      onBack={onBack}
      fullScreen={fullScreen}
      onExit={onExit}
      isOnboardingCompleted={isOnboardingCompleted}
    >
      {showProgressBar && <ProgressBar progressPercentage={state.context.progressPercentage} />}
      <Switch>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.welcome}>
          <Welcome
            onNext={() => send({ type: 'Welcome.Next' })}
            inviteToken={state.context.inviteToken}
            medicalRole={(state.context.careType === CareType.psychiatry
              ? ProviderMedicalRole.psychiatrist
              : ProviderMedicalRole.therapist
            )}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.invalidLink}>
          <InvalidLink />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.createAccount}>
          <CreateAccount
            onNext={data => send({ type: 'CreateAccount.Next', data })}
            onBack={onBack}
            inviteToken={state.context.inviteToken}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.verifyPhone}>
          <VerifyPhone
            onBack={onBack}
            onNext={data => send({ type: 'VerifyPhone.Next', data })}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.verifyPhoneCode}>
          <VerifyPhoneCode
            onBack={onBack}
            onNext={(data) => send({ type: 'VerifyPhoneCode.Next', data })}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.where}>
          <Where
            onNext={data => send({ type: 'Where.Next', data })}
            onNotAvailable={data => send({ type: 'Where.NotAvailable', data })}
            onBack={onBack}
            stateId={state.context.stateId}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.notAvailable}>
          <NotAvailable
            stateId={state.context.stateId}
            onBack={onBack}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.insurance}>
          <Insurance
            onNext={data => send({ type: 'Insurance.Next', data })}
            onBack={onBack}
            insuranceId={state.context.insuranceId}
            isBookingSponsored={eligibility.isBookingSponsored}
            stateId={state.context.stateId}
            title="Do you have health insurance?"
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.age}>
          <Age
            onNext={data => send({ type: 'Age.Next', data })}
            onBack={onBack}
            isBookingForSelf
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.tooYoung}>
          <TooYoung
            onBack={onBack}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.reason}>
          <Reason
            onNext={data => send({ type: 'Reason.Next', data })}
            onBack={onBack}
            specialtyIds={state.context.specialtyIds}
            isBookingForSelf
            isRequired
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.providerFilters}>
          <ProviderFilters
            onNext={data => send({ type: 'ProviderFilters.Next', data })}
            onBack={onBack}
            providerFilters={state.context.showTheseProviderFilters}
            hideAlert
            careType={state.context.careType}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.providerIdentityFilter}>
          <ProviderIdentityFilter
            onNext={data => send({ type: 'ProviderIdentityFilter.Next', data })}
            onBack={onBack}
            identityFilters={state.context.identityFilters}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.providerExperienceFilter}>
          <ProviderExperienceFilter
            onNext={data => send({ type: 'ProviderExperienceFilter.Next', data })}
            onBack={onBack}
            experienceFilters={state.context.experienceFilters}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.matchesLoading}>
          <MatchesLoading
            hasInsurance={eligibility.hasInsurance}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.matches}>
          <Matches
            onSelectProvider={data => send({ type: 'Matches.SelectProvider', data })}
            onUpdateFilters={data => send({ type: 'Matches.UpdateFilters', data })}
            isBookingSponsored={eligibility.isBookingSponsored}
            matchingData={{
              ...state.context,
              availabilityDayOfWeek: state.context.availabilityDayOfWeek,
              availabilityTimeOfDay: state.context.availabilityTimeOfDay,
              seed: state.context.seed,
              ageId: state.context.age?.id,
              medicalRole: state.context.medicalRole,
            }}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.match}>
          <Match
            isSlotUnavailable={state.context.isSlotUnavailable}
            specialtyIds={state.context.specialtyIds}
            selectedProviderId={state.context.selectedProviderId}
            onScheduleSession={data => send({ type: 'Match.ScheduleSession', data })}
            insuranceId={state.context.insuranceId}
            stateId={state.context.stateId}
            matchingPaymentType={state.context.matchingPaymentType}
            isBookingSponsored={eligibility.isBookingSponsored}
            identityFilters={state.context.identityFilters}
            experienceFilters={state.context.experienceFilters}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.aboutYou}>
          <AboutYou
            onNext={() => send({ type: 'AboutYou.Next' })}
            onBack={onBack}
            stateId={state.context.stateId}
            matchId={state.context.matchId}
            banner={banner}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.insuranceInfo}>
          <InsuranceInfo
            onNext={data => send({ type: 'InsuranceInfo.Next', data })}
            onBack={onBack}
            isInviteOrDirectFlow={false}
            providerId={state.context.selectedProviderId}
            insuranceId={state.context.insuranceId}
            banner={banner}
            isBookingSponsored={eligibility.isBookingSponsored}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.paymentInfo}>
          <PaymentInfo
            onNext={() => send({ type: 'PaymentInfo.Next' })}
            onBack={onBack}
            banner={banner}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.confirmBooking}>
          <ConfirmBooking
            onNext={data => send({ type: 'ConfirmBooking.Next', data })}
            onBookingConflict={() => send({ type: 'ConfirmBooking.BookingConflict' })}
            onBack={onBack}
            matchId={state.context.matchId}
            providerId={state.context.selectedProviderId}
            appointment={state.context.appointment}
            stateId={state.context.stateId}
            insuranceId={
              state.context.matchingPaymentType === MatchingProvidersType.cashpay
                ? null
                : state.context.insuranceId
            }
            careType={state.context.careType}
            isBookingSponsored={eligibility.isBookingSponsored}
            termsAccepted={state.context.termsAccepted}
            matchPreferencesData={{
              modalityId: state.context.modalityId,
              ageGroupId: state.context.age?.id,
              visitReasonIds: state.context.specialtyIds,
              insuranceCompanyId: state.context.insuranceId === SELF_PAY_ID
                ? null
                : state.context.insuranceId,
              stateId: state.context.stateId,
              faithIds: state.context.identityFilters?.faithIds,
              ethnicityIds: state.context.identityFilters?.ethnicityIds,
              genderIds: state.context.identityFilters?.genderIds,
              communityExpertise: {
                faithIds: state.context.experienceFilters.faithIds,
                ethnicityIds: state.context.experienceFilters.ethnicityIds,
                generalIds: state.context.experienceFilters.communityIds
              },
            }}
          />
        </Route>
        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.bookingInterstitial}>
          <BookingInterstitialPage
            context={state.context}
            onNext={() => send({ type: 'BookingInterstitial.Next' })}
            onBack={onBack}
          />
        </Route>

        <Route path={BETTERHELP_ONBOARDING_ROUTING_CONFIG.bookingSuccess}>
          <BookingSuccess
            onExit={() => localStorage.removeItem('betterhelp-matching')}
            intakeFormSubmissionId={state.context.intakeFormSubmissionId}
          />
        </Route>
      </Switch>
    </OnboardingLayout>
  );
};
